// services/zaplati.js — конверторът на заплатите: MEMORD1.XML от РЕГАТА →
// редовете на файла за „Импорт на работни заплати" в Конто 6 (ПРОМТ-17).
//
// Чисти функции без достъп до базата — по модела на services/numbering.js:
// настройките на фирмата (партидите) и схемата (config/zaplati.config.json)
// се ПОДАВАТ отвън, затова всичко тук се тества самостоятелно.
//
// Правилата са описани дословно в 01-Текущо/konto-import-zaplati.md и НЕ се
// преизмислят тук: таговете → позиции (т. 3), партидите и аналогиите (т. 4),
// форматът на файла (т. 5), проверките (т. 7). При разминаване печели файлът.
//
// Защо свой разбор на XML и своя кодова таблица, а не пакети: XML-ът на
// РЕГАТА е машинен, плосък и еднообразен (атрибутни елементи без текстово
// съдържание), а Windows-1251 е 128 знака — две малки таблици срещу две нови
// зависимости в проект, който нарочно ги държи четири.

const { round2 } = require('./totals');

// ---- Windows-1251 ----------------------------------------------------------

// Горната половина на cp1251 (0x80–0xFF) по реда на байтовете. Долната
// (0x00–0x7F) съвпада с ASCII. Конто чете файла точно в тази кодировка —
// UTF-8 или BOM чупят кирилицата (т. 9 от спецификацията).
const CP1251_HIGH =
  'ЂЃ‚ѓ„…†‡€‰Љ‹ЊЌЋЏ' +
  'ђ‘’“”•–—\u0098™љ›њќћџ' +
  ' ЎўЈ¤Ґ¦§Ё©Є«¬­®Ї' +
  '°±Ііґµ¶·ё№є»јЅѕї' +
  'АБВГДЕЖЗИЙКЛМНОП' +
  'РСТУФХЦЧШЩЪЫЬЭЮЯ' +
  'абвгдежзийклмноп' +
  'рстуфхцчшщъыьэюя';

const CP1251_BACK = new Map();
for (let i = 0; i < 128; i++) CP1251_BACK.set(CP1251_HIGH.charCodeAt(i), 0x80 + i);

function decodeWin1251(buf) {
  let out = '';
  for (let i = 0; i < buf.length; i++) {
    const b = buf[i];
    out += b < 0x80 ? String.fromCharCode(b) : CP1251_HIGH[b - 0x80];
  }
  return out;
}

function encodeWin1251(str) {
  const out = Buffer.alloc(str.length);
  for (let i = 0; i < str.length; i++) {
    const c = str.charCodeAt(i);
    if (c < 0x80) { out[i] = c; continue; }
    const b = CP1251_BACK.get(c);
    if (b === undefined) {
      // Тиха замяна би стигнала до Конто като „?" в счетоводна статия —
      // по-добре ясен отказ с показан знак и малко контекст.
      const around = str.slice(Math.max(0, i - 10), i + 10);
      throw new Error('Знакът „' + str[i] + '" (в „…' + around + '…") не може да се запише в Windows-1251.');
    }
    out[i] = b;
  }
  return out;
}

// ---- Разборът на MEMORD1.XML ----------------------------------------------

const ENTITIES = { amp: '&', lt: '<', gt: '>', quot: '"', apos: "'" };
function decodeEntities(s) {
  return String(s).replace(/&(amp|lt|gt|quot|apos|#x?[0-9a-fA-F]+);/g, (m, name) => {
    if (name[0] !== '#') return ENTITIES[name];
    const code = name[1] === 'x' || name[1] === 'X' ? parseInt(name.slice(2), 16) : parseInt(name.slice(1), 10);
    return Number.isFinite(code) ? String.fromCodePoint(code) : m;
  });
}

const trim = (v) => String(v == null ? '' : v).trim();

// Обхожда ВСИЧКИ елементи на всякаква дълбочина (т. 2.2): запис е всеки
// елемент с атрибути debit + kredit + suma; обвивките нямат трите и отпадат.
// Не се разчита на имената на обвивките — заплатата е <trudTD><trud …/>.
function parseMemord(buffer) {
  const xml = decodeWin1251(buffer).replace(/<!--[\s\S]*?-->/g, '');
  const parsed = { god: null, mes: null, eik: '', firmName: '', records: [] };

  const tagRe = /<([A-Za-z_][\w.-]*)((?:\s+[\w.-]+\s*=\s*"[^"]*")*)\s*\/?>/g;
  const attrRe = /([\w.-]+)\s*=\s*"([^"]*)"/g;
  let m;
  while ((m = tagRe.exec(xml)) !== null) {
    const tag = m[1];
    const attrs = {};
    let a;
    attrRe.lastIndex = 0;
    while ((a = attrRe.exec(m[2])) !== null) attrs[a[1]] = decodeEntities(a[2]);

    if (tag === 'documents') {
      parsed.god = parseInt(trim(attrs.godina), 10) || null;
      parsed.mes = parseInt(trim(attrs.mesec), 10) || null;
    } else if (tag === 'firma') {
      parsed.eik = trim(attrs.bulstat);          // стойностите идват с интервали (т. 2.1)
      parsed.firmName = trim(attrs.ime_firma);
    } else if ('debit' in attrs && 'kredit' in attrs && 'suma' in attrs) {
      parsed.records.push({
        tag,
        ime: trim(attrs.ime),
        debit: trim(attrs.debit),
        kredit: trim(attrs.kredit),
        suma: round2(parseFloat(trim(attrs.suma)) || 0),
      });
    }
  }
  return parsed;
}

// ---- Тагът → позицията -----------------------------------------------------

// trud е общ за ТД/ДУК/ГД — различава се само по текста в ime, без значение
// от регистъра, с „управление" първо (т. 3.2). Непознат текст е СТОП, не
// предположение: старият конвертор записваше ГД като „РЗаплата" (т. 9).
function resolveTag(tag, ime, config) {
  const spec = config.tags[tag];
  if (!spec) return { unknown: true };
  if (!spec.special) return { spec };
  const t = String(ime || '').toLowerCase();
  for (const kind of config.trudKinds) {
    if (t.includes(kind.contains)) return { spec: kind };
  }
  return { trudUnknown: true };
}

// ---- Партидите (т. 4.3): образец → аналогия → по подразбиране --------------

// Една страна (Дт или Кт) на един ред. Връща партидата и откъде е дошла;
// `stop` — когато образецът на фирмата очаква ДРУГА сметка (т. 7.1.6: ако
// РЕГАТА смени контировката, партидите стават невалидни и файлът би излъгал).
function resolveSide(items, item, side, account, spec, config) {
  const own = items[item];
  const key = side === 'dt' ? 'dt' : 'kt';
  const partKey = side === 'dt' ? 'dtp' : 'ktp';

  if (own && trim(own[key])) {
    if (trim(own[key]) !== account) {
      return { stop: 'Позиция ' + item + ': РЕГАТА дава ' + (side === 'dt' ? 'дебит' : 'кредит') + ' сметка ' + account + ', а в настройките на фирмата е ' + trim(own[key]) + '. Проверете контировката, преди да продължите.' };
    }
    return { part: trim(own[partKey]), from: 'table', ok: own.ok === true };
  }

  // Сродна позиция в СЪЩАТА фирма, само при съвпадаща сметка (т. 4.3.2).
  const near = (config.analogies[item] || {})[key] || [];
  for (const candidate of near) {
    const c = items[candidate];
    if (c && trim(c[key]) === account) return { part: trim(c[partKey]), from: 'analogy', src: candidate };
  }

  // Образецът по подразбиране (т. 4.3.3) — първото съвпадение печели.
  for (const rule of config.defaults) {
    if (rule.side !== side || rule.account !== account) continue;
    if (rule.kinds && !rule.kinds.includes(spec.kind)) continue;
    return { part: rule.part, from: 'default' };
  }
  return { part: '', from: 'default' };
}

// ---- Сглобяването ----------------------------------------------------------

function lastDayOf(god, mes) {
  const d = new Date(Date.UTC(god, mes, 0)).getUTCDate();
  return String(d).padStart(2, '0') + '.' + String(mes).padStart(2, '0') + '.' + god;
}

// Точно 2 знака след десетичната ТОЧКА, без разделител за хиляди (т. 5.3/6).
function fmt2(n) { return round2(n).toFixed(2); }

// parsed (от parseMemord) + настройките на фирмата + схемата + днешната дата
// (дд.мм.гггг) → редовете на файла, СТОП-овете и бележките за проверка.
// При СТОП нищо не се връща за запис — Конто отхвърля файла и при една
// грешка (т. 1), затова по-добре нашият отказ, отколкото неговият.
function buildKonto(parsed, settings, config, today) {
  const items = (settings && settings.items) || {};
  const out = { rows: [], stops: [], notes: [], unknown: [], totals: null };
  const unconfirmed = new Set();
  const M = String(parsed.mes).padStart(2, '0') + '/' + parsed.god;
  const lastDay = lastDayOf(parsed.god, parsed.mes);

  if (!parsed.god || !parsed.mes || parsed.mes < 1 || parsed.mes > 12) {
    out.stops.push('Файлът няма валидни месец и година (<documents godina mesec>) — това MEMORD1.XML от РЕГАТА ли е?');
    return out;
  }
  if (!parsed.records.length) {
    out.stops.push('Във файла няма нито един запис със сметки и сума — това MEMORD1.XML от РЕГАТА ли е?');
    return out;
  }

  for (const rec of parsed.records) {
    const hit = resolveTag(rec.tag, rec.ime, config);
    if (hit.unknown) {
      // Непознат таг с ненулева сума прави файла НЕПЪЛЕН — спира се и се
      // показва списъкът (т. 3.3); новите тагове се добавят в конфигурацията
      // ръчно, след решение за контировката. Никога тихо.
      if (rec.suma !== 0) out.unknown.push({ tag: rec.tag, debit: rec.debit, kredit: rec.kredit, suma: rec.suma, ime: rec.ime });
      continue;
    }
    if (hit.trudUnknown) {
      out.stops.push('Запис „trud" с непознат текст: „' + rec.ime + '" — не е ясно ТД, ДУК или ГД е. Файлът не е записан.');
      continue;
    }
    const spec = hit.spec;
    if (spec.payment || rec.suma === 0) continue;   // каса и нулеви суми не влизат (т. 2.3, т. 3.1)

    const dtSide = resolveSide(items, spec.item, 'dt', rec.debit, spec, config);
    const ktSide = resolveSide(items, spec.item, 'kt', rec.kredit, spec, config);
    let stopped = false;
    for (const side of [dtSide, ktSide]) {
      if (side.stop) { out.stops.push(side.stop); stopped = true; }
    }
    if (stopped) continue;

    // Бележките за проверка (т. 4.3): по една на ред-страна, а „421 без
    // партида" е нормалното положение и не се отбелязва никъде.
    const noteSide = (side, acc, label) => {
      if (acc === '421' && side.part === '') return;
      if (side.from === 'analogy') out.notes.push('ПРОВЕРИ: ' + spec.item + ' ' + label + ' — партида по аналогия с ' + side.src + '.');
      else if (side.from === 'default' && side.part !== '') out.notes.push('ПРОВЕРИ: ' + spec.item + ' ' + label + ' — партида по подразбиране.');
      if (side.from === 'table' && !side.ok) unconfirmed.add(spec.item);
    };
    noteSide(dtSide, rec.debit, 'Дт');
    noteSide(ktSide, rec.kredit, 'Кт');

    const text = (items[spec.item] && trim(items[spec.item].txt)) || spec.txt;
    const name = text.includes('{M}') ? text.replace('{M}', M)
      : (spec.txt.includes('{M}') ? text + ' м.' + M : text);

    // 12-те полета от ръководството на Конто (т. 5.3); 9–11 остават празни.
    out.rows.push([lastDay, rec.debit, dtSide.part, rec.kredit, ktSide.part, fmt2(rec.suma), spec.item, name, '', '', '', today]);
  }

  // Редът на редовете: ВЪЗХОДЯЩО по позиция (ItemId), стабилно — еднакви
  // позиции остават в реда си от XML-а и не се обединяват. Спецификацията
  // (т. 5.2) казва „редът на XML-а", но РЕАЛНИЯТ файл от работещия конвертор
  // (test/fixtures/zaplati-fint-092026.txt, приет от Конто на 01.10.2026) е
  // сортиран по позиция — при противоречие печели файлът, байт по байт.
  out.rows.sort((a, b) => (a[6] < b[6] ? -1 : a[6] > b[6] ? 1 : 0));

  if (out.unknown.length) {
    out.stops.push('Непознати записи в справката (добавят се в схемата след решение за контировката): ' +
      out.unknown.map((u) => u.tag + ' · Дт ' + u.debit + ' / Кт ' + u.kredit + ' · ' + fmt2(u.suma) + (u.ime ? ' · ' + u.ime : '')).join('; '));
  }
  if (!out.stops.length && !out.rows.length) out.stops.push('След правилата не остана нито един ред — файл не се прави.');
  if (unconfirmed.size) out.notes.push('Непотвърдени партиди в таблицата: ' + Array.from(unconfirmed).sort().join(', ') + '.');
  // СТОП означава „нищо не се записва" (т. 7.1): и редовете падат, за да
  // няма изкушение непълен файл да стигне до Конто.
  if (out.stops.length) { out.rows = []; out.totals = null; }
  else out.totals = totalsOf(out.rows);
  return out;
}

// Сверката от т. 7.2 — по дебит и кредит сметка, 455 по партиди (срещу Д6)
// и 454 общо. Смята се от готовите редове, за да сверява ФАЙЛА, не входа.
function totalsOf(rows) {
  const byDt = {}, byKt = {}, k455 = {};
  let k454 = 0, total = 0;
  for (const r of rows) {
    const sum = parseFloat(r[5]);
    byDt[r[1]] = round2((byDt[r[1]] || 0) + sum);
    byKt[r[3]] = round2((byKt[r[3]] || 0) + sum);
    if (r[3] === '455') k455[r[4] || '(без партида)'] = round2((k455[r[4] || '(без партида)'] || 0) + sum);
    if (r[3] === '454') k454 = round2(k454 + sum);
    total = round2(total + sum);
  }
  return { byDt, byKt, k455, k454, total, rows: rows.length };
}

// ---- Файлът ----------------------------------------------------------------

// TAB между 12-те полета, CRLF след ВСЕКИ ред (и последния), без заглавен
// ред, редът на записите от XML-а, без обединяване (т. 5.2).
function renderTxt(rows) {
  return rows.map((r) => r.join('\t') + '\r\n').join('');
}

function makeFilename(mes, god, eik) {
  return 'ZAPLATI_' + String(mes).padStart(2, '0') + god + '_' + eik + '.TXT';
}

function todaySofia() {
  const d = new Date(new Date().toLocaleString('en-US', { timeZone: 'Europe/Sofia' }));
  return String(d.getDate()).padStart(2, '0') + '.' + String(d.getMonth() + 1).padStart(2, '0') + '.' + d.getFullYear();
}

module.exports = { decodeWin1251, encodeWin1251, parseMemord, resolveTag, resolveSide, buildKonto, totalsOf, renderTxt, makeFilename, lastDayOf, fmt2, todaySofia };
